import { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import { ThemePalette } from 'mui.theme';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { calculatePercentageChange } from '../../FleetOverview/helpers/asset.helpers';
import { ItemCollapse } from './ItemCollapse/ItemCollapse';
import type { DataEntry, HistogramData } from 'types/FleetOverview.types';
import type { Asset } from 'types/asset.types';
import { useAppDispatch } from 'store/hook';
import { removeTagFromAssetComparisonByTags } from 'store/asset.slice';
import { truncateTo2dp } from 'utils/helpers/general';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const ListViewItem = ({
  enableDarkTheme,
  title,
  data,
  assets,
  thresholds,
  defaultThresholds,
  tagIdToLabelMap,
}: {
  enableDarkTheme: boolean;
  title: string;
  data: {
    data: DataEntry[];
    histogram: HistogramData[];
    unit?: string;
  };
  assets: Asset[];
  thresholds: any[];
  defaultThresholds: any[];
  tagIdToLabelMap?: Record<string, string>;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const { data: chartData, histogram } = data;
  const [itemOpen, setOpen] = useState(false);
  const dispatch = useAppDispatch();

  const handleClick = () => {
    setOpen((prev) => !prev);
  };

  const padArrayToFour = (data: any[], defaultValue = null) => {
    const newData = [...data]; // Create a shallow copy of the array
    while (newData.length < 4) {
      newData.push(defaultValue);
    }
    return newData;
  };

  const getColor = (title: string, isIncrement: boolean) => {
    if (title === 'fuel usage') {
      if (!isIncrement) {
        return ThemePalette.typography.success;
      } else {
        return ThemePalette.typography.danger;
      }
    } else {
      if (isIncrement) {
        return ThemePalette.typography.success;
      } else {
        return ThemePalette.typography.danger;
      }
    }
  };

  // Use the padArrayToFour function to ensure the array length is 4
  const paddedArray = padArrayToFour(chartData);

  return (
    <Accordion
      expanded={itemOpen}
      onChange={handleClick}
      sx={{
        mb: 3,
        backgroundColor: enableDarkTheme
          ? palette.boxBackground
          : palette.boxBackground,
        borderRadius: '16px !important',
        border: `1px solid ${
          enableDarkTheme ? palette.cardBorderColor : 'rgba(0,0,0,0.12)'
        }`,
        boxShadow: enableDarkTheme
          ? '0 4px 12px rgba(0,0,0,0.3)'
          : '0 2px 8px rgba(0,0,0,0.1)',
        background: enableDarkTheme
          ? palette.boxBackground
          : `linear-gradient(145deg, ${palette.boxBackground} 0%, rgba(245, 248, 250, 0.95) 100%)`,
        overflow: 'hidden',
        transition: 'all 0.25s ease-in-out',
        '&:hover': {
          boxShadow: enableDarkTheme
            ? '0 6px 16px rgba(0,0,0,0.4)'
            : '0 6px 16px rgba(0,0,0,0.15)',
          borderColor: enableDarkTheme
            ? palette.cardBorderColor
            : 'rgba(0,0,0,0.18)',
        },
        '&:before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          margin: '0 0 20px 0',
        },
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon
            sx={{
              color: palette.textPrimary,
              fontSize: '1.2rem',
              transition: 'transform 0.3s ease',
              // transform: itemOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        }
        sx={{
          backgroundColor: 'transparent',
          borderRadius: '16px',
          minHeight: '90px',
          padding: '8px 8px 8px 16px',
          '&.Mui-expanded': {
            minHeight: '90px',
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
          '& .MuiAccordionSummary-content': {
            margin: '16px 0',
            alignItems: 'center',
          },
          '&:hover': {
            backgroundColor: enableDarkTheme
              ? 'rgba(255, 255, 255, 0.03)'
              : 'rgba(0, 0, 0, 0.01)',
          },
        }}
      >
        <Box sx={{ width: '100%' }}>
          {/* Header Section */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              pb: 1.5,
              borderBottom: `1px solid ${
                enableDarkTheme
                  ? 'rgba(255, 255, 255, 0.06)'
                  : 'rgba(0, 0, 0, 0.04)'
              }`,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: palette.textPrimary,
                fontWeight: 700,
                textTransform: 'capitalize',
                display: 'flex',
                alignItems: 'center',
                letterSpacing: '0.015em',
                '&::first-letter': {
                  fontSize: '1.1em',
                },
              }}
            >
              {tagIdToLabelMap && title in tagIdToLabelMap
                ? tagIdToLabelMap[title]
                : title.replace(/\b\w/g, (char) => char.toUpperCase())}
              <Chip
                label={`${
                  paddedArray.filter((item) => item !== null).length - 1
                } Assets`}
                size="small"
                sx={{
                  ml: 2,
                  backgroundColor: enableDarkTheme
                    ? 'rgba(58, 70, 87, 0.8)'
                    : 'rgba(0, 0, 0, 0.06)',
                  color: enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.9)'
                    : 'rgba(0, 0, 0, 0.8)',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  padding: '2px 8px',
                  height: '22px',
                  borderRadius: '12px',
                }}
              />
            </Typography>

            {![
              'utilization',
              'total engine hours',
              'fuel usage',
              'productivity',
            ].includes(title) && (
              <Tooltip title={t('remove_this_metric')}>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(removeTagFromAssetComparisonByTags(title));
                  }}
                  sx={{
                    color: '#ff0000',
                    width: '36px',
                    height: '36px',
                    '&:hover': {
                      color: '#FF0000',
                      backgroundColor: enableDarkTheme
                        ? 'rgba(255, 0, 0, 0.15)'
                        : 'rgba(255, 0, 0, 0.08)',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Metrics Cards */}
          <Grid container spacing={2.5}>
            {paddedArray.map((item: DataEntry, index: number) => {
              const { percentageChange, isIncrement } =
                calculatePercentageChange(
                  chartData[0]?.value,
                  item?.value || 0
                );
              if (item === null) {
                return (
                  <Grid item xl={3} lg={3} md={6} xs={12} key={index}>
                    <Card
                      sx={{
                        backgroundColor: enableDarkTheme
                          ? 'rgba(58, 70, 87, 0.2)'
                          : 'rgba(0,0,0,0.03)',
                        border: `1px dashed ${
                          enableDarkTheme
                            ? 'rgba(58, 70, 87, 0.6)'
                            : 'rgba(0,0,0,0.15)'
                        }`,
                        minHeight: '95px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '12px',
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: enableDarkTheme
                            ? 'rgba(255,255,255,0.4)'
                            : 'rgba(0,0,0,0.4)',
                          fontStyle: 'italic',
                          letterSpacing: '0.02em',
                        }}
                      >
                        {t('no_data')}
                      </Typography>
                    </Card>
                  </Grid>
                );
              }

              return (
                <Grid item xl={3} lg={3} md={6} xs={12} key={index}>
                  <Card
                    elevation={0}
                    sx={{
                      backgroundColor: enableDarkTheme
                        ? palette.toolBarBackground
                        : palette.toolBarBackground,
                      background: enableDarkTheme
                        ? palette.toolBarBackground
                        : `linear-gradient(145deg, ${palette.toolBarBackground} 0%, rgba(250, 252, 255, 0.95) 100%)`,
                      border: `1px solid ${
                        enableDarkTheme
                          ? palette.cardBorderColor
                          : 'rgba(0,0,0,0.08)'
                      }`,
                      borderRadius: '12px',
                      minHeight: '95px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: enableDarkTheme
                          ? '0 4px 8px rgba(0,0,0,0.25)'
                          : '0 4px 8px rgba(0,0,0,0.1)',
                        borderColor: enableDarkTheme
                          ? palette.cardBorderColor
                          : 'rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        py: 2,
                        height: '100%',
                        '&:last-child': {
                          paddingBottom: '16px',
                        },
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.35rem',
                          color: palette.textPrimary,
                          mb: 0.5,
                          textShadow: enableDarkTheme
                            ? '0 1px 2px rgba(0,0,0,0.3)'
                            : 'none',
                        }}
                      >
                        {title === 'utilization'
                          ? truncateTo2dp(item.value * 100)
                          : item.value}
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{
                            ml: 0.5,
                            fontSize: '12px',
                            opacity: 0.7,
                            fontWeight: 400,
                          }}
                        >
                          {title === 'utilization'
                            ? '%'
                            : data.unit
                            ? data.unit
                            : ''}
                        </Typography>
                      </Typography>

                      {index !== 0 && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          {isIncrement ? (
                            <TrendingUpIcon
                              sx={{
                                fontSize: '16px',
                                color: getColor(title, isIncrement),
                                filter:
                                  'drop-shadow(0 1px 1px rgba(0,0,0,0.1))',
                              }}
                            />
                          ) : (
                            <TrendingDownIcon
                              sx={{
                                fontSize: '16px',
                                color: getColor(title, isIncrement),
                                filter:
                                  'drop-shadow(0 1px 1px rgba(0,0,0,0.1))',
                              }}
                            />
                          )}
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '12px',
                              fontWeight: 600,
                              color: getColor(title, isIncrement),
                              letterSpacing: '0.02em',
                            }}
                          >
                            {percentageChange}%
                          </Typography>
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          backgroundColor: enableDarkTheme
            ? palette.pageBackground
            : palette.pageBackground,
          borderTop: `1px solid ${
            enableDarkTheme ? palette.cardBorderColor : 'rgba(0,0,0,0.09)'
          }`,
          borderBottomLeftRadius: '16px',
          borderBottomRightRadius: '16px',
          p: 3,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: enableDarkTheme
              ? 'linear-gradient(90deg, rgba(58,70,87,0.5) 0%, rgba(58,70,87,0.3) 100%)'
              : 'linear-gradient(90deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.02) 100%)',
          },
        }}
      >
        <ItemCollapse
          enableDarkTheme={enableDarkTheme}
          data={histogram}
          assets={assets}
          thresholds={thresholds}
          title={title}
          defaultThresholds={defaultThresholds}
        />
      </AccordionDetails>
    </Accordion>
  );
};
